import { useSyncExternalStore } from 'react';
/** Answers of the proxy, not of the API: the API itself was not reached. */
const GATEWAY_FAILURES = new Set([502, 504]);
// Only a browser that says so is offline; where `onLine` is not known, the requests will tell.
let state = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online';
const listeners = new Set();
function set(next) {
    if (next === state)
        return;
    state = next;
    for (const listener of listeners)
        listener();
}
/** What a request showed about the connection: an answer, or a failure on the way. */
export function reportAnswer(status) {
    set(GATEWAY_FAILURES.has(status) ? 'unreachable' : 'online');
}
export function reportFailure() {
    // Without a network the browser's word is the better one: it says when the network is back.
    if (state !== 'offline')
        set('unreachable');
}
/** The browser's own word on its network (`online` and `offline` events). */
export function reportNetwork(online) {
    // Back on the network does not mean Kanos answers: the next request will tell.
    set(online ? 'online' : 'offline');
}
/** For tests: back to the start. */
export function resetConnection() {
    state = 'online';
}
export function connection() {
    return state;
}
/**
 * `fetch` that reports how each request went - the app's API client sends every request
 * through it. Aborted requests say nothing about the connection.
 */
export const watchedFetch = async (input, init) => {
    try {
        const response = await fetch(input, init);
        reportAnswer(response.status);
        return response;
    }
    catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError'))
            reportFailure();
        throw error;
    }
};
/** Listens to the browser's network events, once per app (`mountApp`). */
export function watchNetwork() {
    window.addEventListener('online', () => reportNetwork(true));
    window.addEventListener('offline', () => reportNetwork(false));
}
const subscribe = (listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
/** The connection as it is now, for a component that shows it. */
export const useConnection = () => useSyncExternalStore(subscribe, connection, () => 'online');
//# sourceMappingURL=connection.js.map